import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Snackbar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  PauseCircle as PauseIcon,
  PlayCircle as PlayIcon,
  Person as PersonIcon,
  Block as DisqualifyIcon,
  CheckCircle as QualifyIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  School as CoachIcon,
  Business as ClubIcon,
  Badge as BadgeIcon,
  Cake as BirthdayIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  Verified as VerifiedIcon,
  EventAvailable as ExpirationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonAdd as PersonAddIcon,
  EmojiEvents as TrophyIcon,
  Timeline as TimelineIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { REGISTRATION_BASE_URL, API_BASE_URL, getApiBaseUrlWithFallback } from '../config/environment';
import { ORDERED_SKATING_LEVELS } from '../utils/skatingLevels';
import { formatCompetitionDateRange } from '../utils/eligibilityDisplay';

const filterOptions = [
  { value: 'all', label: 'All Skaters' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paused', label: 'Paused' },
  { value: 'disqualified', label: 'Disqualified' },
  { value: 'championshipEligible', label: 'Championship eligible' },
  { value: 'championshipIssues', label: 'Championship issues' },
];

const CHAMPIONSHIP_ISSUE_COLORS = {
  'Paid late': { bgcolor: 'warning.50', color: 'warning.dark', borderColor: 'warning.200' },
  'Excel / non-FS': { bgcolor: 'warning.50', color: 'warning.dark', borderColor: 'warning.200' },
  Unpaid: { bgcolor: 'warning.50', color: 'warning.dark', borderColor: 'warning.200' },
  'Need 3 clubs': { bgcolor: 'grey.50', color: 'text.secondary', borderColor: 'grey.300' },
};

function ChampionshipStatusChips({ skater, selectedSeason }) {
  if (!selectedSeason) return null;
  if (skater.championshipEligible) {
    return (
      <Tooltip title="3 unique counting clubs with qualifying free skate scores">
        <Chip
          icon={<TrophyIcon sx={{ fontSize: 14 }} />}
          label="Eligible"
          size="small"
          sx={{
            fontWeight: 600,
            bgcolor: 'success.50',
            color: 'success.dark',
            border: '1px solid',
            borderColor: 'success.200',
            '& .MuiChip-icon': { color: 'success.main' },
          }}
        />
      </Tooltip>
    );
  }
  if (!skater.championshipWarning || skater.championshipWarning === 'Paused' || skater.championshipWarning === 'Disqualified') {
    return null;
  }
  const colors = CHAMPIONSHIP_ISSUE_COLORS[skater.championshipWarning] || CHAMPIONSHIP_ISSUE_COLORS['Need 3 clubs'];
  return (
    <Tooltip title={skater.championshipWarningDetail || skater.championshipWarning}>
      <Chip
        icon={<WarningIcon sx={{ fontSize: 14 }} />}
        label={skater.championshipWarning}
        size="small"
        sx={{
          fontWeight: 600,
          bgcolor: colors.bgcolor,
          color: colors.color,
          border: '1px solid',
          borderColor: colors.borderColor,
          '& .MuiChip-icon': { color: colors.color },
        }}
      />
    </Tooltip>
  );
}

const ALL_LEVELS = ORDERED_SKATING_LEVELS;

export default function InterclubSkaters({ interclubId, interclub }) {
  const { getIdToken, user } = useFirebaseAuth();
  const { getHeaders: getImpersonationHeaders } = useImpersonation();
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeMyOnly = searchParams.get('scope') === 'my';

  const [skaters, setSkaters] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');
  const [filter, setFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState(() => searchParams.get('level') || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkater, setSelectedSkater] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [editForm, setEditForm] = useState({
    clubId: '',
    currentLevel: '',
    coaches: [],
  });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({ total: 0, paid: 0, unpaid: 0, paused: 0, championshipEligible: 0, championshipIssues: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [unregisteredCompetitors, setUnregisteredCompetitors] = useState([]);
  const [unregisteredLoading, setUnregisteredLoading] = useState(false);
  const [showUnregistered, setShowUnregistered] = useState(false);
  const [unregisteredSearch, setUnregisteredSearch] = useState('');
  const [skaterEligibility, setSkaterEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [showCompetitions, setShowCompetitions] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadSeasons = useCallback(async () => {
    try {
      const baseUrl = await getApiBaseUrlWithFallback();
      const response = await fetch(`${baseUrl}/seasons`);
      if (response.ok) {
        const data = await response.json();
        const sorted = (data || []).sort((a, b) => (a.startDate?._seconds || 0) - (b.startDate?._seconds || 0));
        setSeasons(sorted);
        const current = sorted.find((season) => season.isCurrent);
        if (current) setSelectedSeason(current.id);
      }
    } catch (err) {
      console.error('Error loading seasons:', err);
    }
  }, []);

  const loadClubs = useCallback(async () => {
    if (!interclubId) return;
    try {
      const baseUrl = await getApiBaseUrlWithFallback();
      const seriesParam = scopeMyOnly ? '' : '?series=true';
      const headers = {};
      if (seriesParam) {
        const token = await getIdToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(`${baseUrl}/interclubs/${interclubId}/clubs${seriesParam}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setClubs(data || []);
      }
    } catch (err) {
      console.error('Error loading clubs:', err);
    }
  }, [interclubId, scopeMyOnly, getIdToken]);

  const loadCoaches = useCallback(async () => {
    if (!interclubId) return;
    try {
      const token = await getIdToken();
      if (!token) return;
      const seriesParam = scopeMyOnly ? '' : '?series=true';
      const syncedParam = 'includeSyncedCoaches=true'; // so Edit Skater dropdown includes synced USFS coaches
      const sep = seriesParam ? '&' : '?';
      const response = await fetch(`${REGISTRATION_BASE_URL}/interclub/${interclubId}/coaches${seriesParam}${sep}${syncedParam}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...getImpersonationHeaders(),
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCoaches(data.coaches || []);
      }
    } catch (err) {
      console.error('Error loading coaches:', err);
    }
  }, [interclubId, getIdToken, getImpersonationHeaders, scopeMyOnly]);

  const loadSkaters = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      let url = `${REGISTRATION_BASE_URL}/interclub/${interclubId}/skaters`;
      const params = new URLSearchParams();
      if (selectedClub) params.set('clubId', selectedClub);
      if (selectedSeason) params.set('seasonId', selectedSeason);
      if (!scopeMyOnly) params.set('series', 'true');
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: { 
          Authorization: `Bearer ${token}`,
          ...getImpersonationHeaders(),
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch skaters');
      }
      
      const data = await response.json();
      setSkaters(data.skaters || []);
      setStats(data.stats || { total: 0, paid: 0, unpaid: 0, paused: 0, championshipEligible: 0, championshipIssues: 0 });
    } catch (err) {
      console.error('Error loading skaters:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [interclubId, selectedClub, selectedSeason, getIdToken, scopeMyOnly]);

  const loadUnregisteredCompetitors = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      setUnregisteredLoading(true);
      const baseUrl = await getApiBaseUrlWithFallback();
      const params = new URLSearchParams();
      if (interclubId) params.set('interclubId', interclubId);
      if (selectedSeason) params.set('seasonId', selectedSeason);
      
      const response = await fetch(`${baseUrl}/unregistered-competitors?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setUnregisteredCompetitors(data.unregisteredCompetitors || []);
      }
    } catch (err) {
      console.error('Error loading unregistered competitors:', err);
    } finally {
      setUnregisteredLoading(false);
    }
  }, [interclubId, selectedSeason]);

  const loadSkaterEligibility = useCallback(async (skaterId) => {
    if (!skaterId) return;
    
    try {
      setEligibilityLoading(true);
      setSkaterEligibility(null);
      const token = await getIdToken();
      const baseUrl = await getApiBaseUrlWithFallback();
      const params = new URLSearchParams();
      if (selectedSeason) params.set('seasonId', selectedSeason);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${baseUrl}/skaters/${skaterId}/eligibility${qs}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...getImpersonationHeaders(),
        },
      });
      if (response.ok) {
        const data = await response.json();
        const eligibility = data.eligibility || {};
        const competitionsAttended = data.competitionsAttended || [];
        setSkaterEligibility({
          ...eligibility,
          competitionsAttended,
          requirements: data.requirements || {},
          qualifyingEventsCount: competitionsAttended.filter((c) => c.isEligible).length,
        });
      }
    } catch (err) {
      console.error('Error loading skater eligibility:', err);
    } finally {
      setEligibilityLoading(false);
    }
  }, [getIdToken, getImpersonationHeaders, selectedSeason]);

  useEffect(() => {
    loadSeasons();
    loadClubs();
    loadCoaches();
  }, [loadSeasons, loadClubs, loadCoaches]);

  useEffect(() => {
    if (selectedSeason) {
      loadSkaters();
    }
  }, [loadSkaters, selectedSeason]);

  useEffect(() => {
    if (showUnregistered && selectedSeason) {
      loadUnregisteredCompetitors();
    }
  }, [showUnregistered, selectedSeason, loadUnregisteredCompetitors]);

  // Sync level filter from URL when navigating from dashboard level chips
  useEffect(() => {
    const levelFromUrl = searchParams.get('level') || '';
    setLevelFilter(levelFromUrl);
  }, [searchParams]);

  const handleExportReport = async () => {
    if (!interclubId || !selectedSeason) return;
    setExporting(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Unable to get authentication token');
      const baseUrl = await getApiBaseUrlWithFallback();
      const params = new URLSearchParams({ seasonId: selectedSeason });
      if (!scopeMyOnly) params.set('series', 'true');
      if (selectedClub) params.set('clubId', selectedClub);
      const response = await fetch(
        `${baseUrl}/interclubs/${interclubId}/skater-competition-report?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}`, ...getImpersonationHeaders() } }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Failed to export report (${response.status})`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      a.download = match?.[1] || `skater-competition-report-${selectedSeason}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'Skater competition report downloaded.', severity: 'success' });
    } catch (err) {
      console.error('Error exporting skater report:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to export report', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleTogglePause = async (skater) => {
    const isPaused = skater.isPaused;
    const action = isPaused ? 'unpause' : 'pause';
    const actionLabel = isPaused ? 'activate' : 'pause';

    if (!window.confirm(`Are you sure you want to ${actionLabel} ${skater.firstName} ${skater.lastName} for this season?`)) {
      return;
    }

    try {
      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/admin/skaters/${skater.id}/${action}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...getImpersonationHeaders(),
        },
        body: JSON.stringify({ seasonId: selectedSeason }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${action} skater`);
      }

      setSnackbar({ 
        open: true, 
        message: `${skater.firstName} ${skater.lastName} ${isPaused ? 'activated' : 'paused'} for this season`, 
        severity: 'success' 
      });
      loadSkaters();
    } catch (err) {
      console.error(`Error ${action}ing skater:`, err);
      setSnackbar({ open: true, message: err.message || `Failed to ${action} skater`, severity: 'error' });
    }
  };

  const handleToggleDisqualify = async (skater) => {
    const isDisqualified = skater.isDisqualified;
    const action = isDisqualified ? 'Remove disqualification from' : 'Disqualify';
    const confirmMessage = isDisqualified
      ? `Are you sure you want to remove the disqualification from ${skater.firstName} ${skater.lastName}?`
      : `Are you sure you want to DISQUALIFY ${skater.firstName} ${skater.lastName} for this season?\n\nThis will prevent them from qualifying for championships.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = await getIdToken(true);
      const response = await fetch(`${REGISTRATION_BASE_URL}/skaters/${skater.id}/disqualify`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...getImpersonationHeaders(),
        },
        body: JSON.stringify({ 
          seasonId: selectedSeason,
          disqualified: !isDisqualified,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update disqualification status');
      }

      setSnackbar({ 
        open: true, 
        message: `${skater.firstName} ${skater.lastName} ${isDisqualified ? 'disqualification removed' : 'disqualified'} for this season`, 
        severity: 'success' 
      });
      loadSkaters();
    } catch (err) {
      console.error('Error updating disqualification:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to update disqualification', severity: 'error' });
    }
  };

  const handleSkaterClick = (skater) => {
    setSelectedSkater(skater);
    setDetailDialogOpen(true);
    setShowCompetitions(true);
    setSkaterEligibility(null);
    if (skater?.id) {
      loadSkaterEligibility(skater.id);
    }
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedSkater(null);
    setShowCompetitions(false);
    setSkaterEligibility(null);
  };

  const handleShowCompetitions = () => {
    if (!showCompetitions && selectedSkater?.id) {
      loadSkaterEligibility(selectedSkater.id);
    }
    setShowCompetitions(!showCompetitions);
  };

  const handleEditClick = (skater) => {
    setSelectedSkater(skater);
    const currentLevel = skater.currentLevel || skater.startingLevel || '';
    setEditForm({
      clubId: skater.clubId || '',
      currentLevel,
      coaches: skater.coaches || [],
    });
    setDetailDialogOpen(false);
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setSelectedSkater(null);
    setEditForm({ clubId: '', currentLevel: '', coaches: [] });
  };

  const handleSaveEdit = async () => {
    if (!selectedSkater) return;

    try {
      setSaving(true);
      const token = await getIdToken();
      if (!token) throw new Error('Unable to get authentication token');

      // Get club name for the selected clubId
      const selectedClubData = clubs.find(c => c.id === editForm.clubId);
      const clubName = selectedClubData?.name || '';

      // Build the update payload (interclub admins cannot create Pre-Preliminary acknowledgements)
      const updatePayload = {
        clubId: editForm.clubId,
        club: clubName,
        currentLevel: editForm.currentLevel,
        coaches: editForm.coaches,
      };

      const response = await fetch(`${REGISTRATION_BASE_URL}/skaters/${selectedSkater.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...getImpersonationHeaders(),
        },
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update skater');
      }

      setSnackbar({ 
        open: true, 
        message: `${selectedSkater.firstName} ${selectedSkater.lastName} updated successfully`, 
        severity: 'success' 
      });
      handleCloseEdit();
      loadSkaters();
    } catch (err) {
      console.error('Error updating skater:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to update skater', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const filteredSkaters = skaters.filter(skater => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${skater.firstName || ''} ${skater.lastName || ''}`.toLowerCase();
    const matchesSearch = (
      fullName.includes(searchLower) ||
      skater.membershipId?.toLowerCase().includes(searchLower) ||
      skater.clubName?.toLowerCase().includes(searchLower)
    );
    
    if (!matchesSearch) return false;

    if (levelFilter) {
      const skaterLevel = (skater.currentLevel || skater.startingLevel || '').trim();
      const matchesLevel = levelFilter === 'Unknown'
        ? !skaterLevel || skaterLevel === 'Unknown'
        : skaterLevel === levelFilter;
      if (!matchesLevel) return false;
    }
    
    switch (filter) {
      case 'paid':
        return skater.paid;
      case 'unpaid':
        return !skater.paid && !skater.isPaused;
      case 'paused':
        return skater.isPaused;
      case 'disqualified':
        return skater.isDisqualified;
      case 'championshipEligible':
        return Boolean(skater.championshipEligible);
      case 'championshipIssues':
        return !skater.championshipEligible && Boolean(skater.championshipWarning);
      default:
        return true;
    }
  });

  const levelOptions = useMemo(() => {
    const levels = new Set();
    skaters.forEach((s) => {
      const level = (s.currentLevel || s.startingLevel || '').trim();
      levels.add(level || 'Unknown');
    });
    const list = Array.from(levels);
    const unknown = list.includes('Unknown') ? ['Unknown'] : [];
    const known = list.filter((l) => l !== 'Unknown' && ALL_LEVELS.includes(l));
    const other = list.filter((l) => l !== 'Unknown' && !ALL_LEVELS.includes(l));
    const sortedKnown = ALL_LEVELS.filter((l) => known.includes(l));
    return [...unknown, ...sortedKnown, ...other];
  }, [skaters]);

  const handleLevelFilterChange = (value) => {
    setLevelFilter(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('level', value);
      else next.delete('level');
      return next;
    });
  };

  const showInterclubColumn = useMemo(() => skaters.some((s) => s.interclubName), [skaters]);

  const sortedSkaters = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filteredSkaters].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          bVal = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'membershipId':
          aVal = (a.membershipId || '').toLowerCase();
          bVal = (b.membershipId || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'club':
          aVal = (a.clubName || a.club || '').toLowerCase();
          bVal = (b.clubName || b.club || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'level':
          aVal = (a.currentLevel || a.startingLevel || '').toLowerCase();
          bVal = (b.currentLevel || b.startingLevel || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'coach': {
          const coachStr = (s) => {
            if (Array.isArray(s.coaches) && s.coaches.length > 0) {
              const c = s.coaches[0];
              return (typeof c === 'string' ? c : (c?.displayName || c?.name || '')).toLowerCase();
            }
            return (s.coachName || '').toLowerCase();
          };
          aVal = coachStr(a);
          bVal = coachStr(b);
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        }
        case 'interclub':
          aVal = (a.interclubName || '').toLowerCase();
          bVal = (b.interclubName || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'points': {
          aVal = Number(a.totalPoints) || 0;
          bVal = Number(b.totalPoints) || 0;
          return dir * (aVal - bVal);
        }
        default:
          return 0;
      }
    });
  }, [filteredSkaters, sortField, sortDirection]);

  const handleSort = (field) => {
    setSortField(field);
    setSortDirection((prev) => (prev === 'asc' && sortField === field ? 'desc' : 'asc'));
  };

  const filteredUnregistered = useMemo(() => {
    if (!unregisteredSearch) return unregisteredCompetitors;
    const searchLower = unregisteredSearch.toLowerCase();
    return unregisteredCompetitors.filter((c) => {
      const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
      return fullName.includes(searchLower) || c.clubs.some((club) => club.toLowerCase().includes(searchLower));
    });
  }, [unregisteredCompetitors, unregisteredSearch]);

  return (
    <>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}>
              <PersonIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Skaters
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {scopeMyOnly ? 'Skaters in your interclub only' : 'Skaters across all interclubs in the series'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup
              value={scopeMyOnly ? 'my' : 'all'}
              exclusive
              onChange={(_, value) => {
                if (value == null) return;
                const next = new URLSearchParams(searchParams);
                if (value === 'my') next.set('scope', 'my');
                else next.delete('scope');
                setSearchParams(next);
              }}
              size="small"
            >
              <ToggleButton value="all">All interclubs</ToggleButton>
              <ToggleButton value="my">{interclub?.name ? `${interclub.name} only` : 'My interclub only'}</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              onClick={handleExportReport}
              disabled={!selectedSeason || exporting}
            >
              {exporting ? 'Exporting…' : 'Export CSV report'}
            </Button>
            <IconButton 
              onClick={loadSkaters} 
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Stats */}
        {selectedSeason && (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip 
              label={`Total: ${stats.total}`} 
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Chip 
              label={`Paid: ${stats.paid}`} 
              sx={{ bgcolor: 'success.50', color: 'success.dark', fontWeight: 600, border: '1px solid', borderColor: 'success.200' }}
            />
            <Chip 
              label={`Unpaid: ${stats.unpaid}`} 
              sx={{ bgcolor: 'warning.50', color: 'warning.dark', fontWeight: 600, border: '1px solid', borderColor: 'warning.200' }}
            />
            <Chip 
              label={`Paused: ${stats.paused}`} 
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Chip
              icon={<TrophyIcon sx={{ fontSize: 16 }} />}
              label={`Eligible: ${stats.championshipEligible || 0}`}
              onClick={() => setFilter('championshipEligible')}
              sx={{ bgcolor: 'success.50', color: 'success.dark', fontWeight: 600, border: '1px solid', borderColor: 'success.200', '& .MuiChip-icon': { color: 'success.main' } }}
            />
            <Chip
              icon={<WarningIcon sx={{ fontSize: 16 }} />}
              label={`Issues: ${stats.championshipIssues || 0}`}
              onClick={() => setFilter('championshipIssues')}
              sx={{ bgcolor: 'warning.50', color: 'warning.dark', fontWeight: 600, border: '1px solid', borderColor: 'warning.200', '& .MuiChip-icon': { color: 'warning.main' } }}
            />
            {levelFilter && (
              <Chip
                label={`Level: ${levelFilter}`}
                onDelete={() => handleLevelFilterChange('')}
                sx={{ fontWeight: 600 }}
                size="small"
              />
            )}
          </Box>
        )}

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Season</InputLabel>
              <Select
                value={selectedSeason}
                onChange={(e) => setSelectedSeason(e.target.value)}
                label="Season"
                sx={{ borderRadius: 2 }}
              >
                {seasons.map((season) => (
                  <MenuItem key={season.id} value={season.id}>
                    {season.name} {season.isCurrent && '(Current)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Club</InputLabel>
              <Select
                value={selectedClub}
                onChange={(e) => setSelectedClub(e.target.value)}
                label="Club"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Clubs</MenuItem>
                {clubs.map((club) => (
                  <MenuItem key={club.id} value={club.id}>
                    {club.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                label="Status"
                sx={{ borderRadius: 2 }}
              >
                {filterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Level</InputLabel>
              <Select
                value={levelFilter}
                onChange={(e) => handleLevelFilterChange(e.target.value)}
                label="Level"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Levels</MenuItem>
                {levelOptions.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, membership ID, or club..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
            <Button onClick={loadSkaters} sx={{ ml: 2 }} size="small">
              Retry
            </Button>
          </Alert>
        )}

        {!selectedSeason ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3, bgcolor: 'grey.50', borderRadius: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Please select a season to view skater registration status.
            </Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredSkaters.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3, bgcolor: 'grey.50', borderRadius: 3 }}>
            <PersonIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm || filter !== 'all' || levelFilter ? 'No skaters found' : 'No skaters yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 'Try adjusting your search terms.' : levelFilter ? `No skaters at level "${levelFilter}". Clear the level filter to see all.` : 'Skaters from your interclub clubs will appear here.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 2,
            '& .MuiTableCell-head': {
              bgcolor: 'grey.50',
              fontWeight: 700
            }
          }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortField === 'name' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'membershipId' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'membershipId'}
                      direction={sortField === 'membershipId' ? sortDirection : 'asc'}
                      onClick={() => handleSort('membershipId')}
                    >
                      Membership ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'club' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'club'}
                      direction={sortField === 'club' ? sortDirection : 'asc'}
                      onClick={() => handleSort('club')}
                    >
                      Club
                    </TableSortLabel>
                  </TableCell>
                  {showInterclubColumn && (
                    <TableCell sortDirection={sortField === 'interclub' ? sortDirection : false}>
                      <TableSortLabel
                        active={sortField === 'interclub'}
                        direction={sortField === 'interclub' ? sortDirection : 'asc'}
                        onClick={() => handleSort('interclub')}
                      >
                        Interclub
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell sortDirection={sortField === 'level' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'level'}
                      direction={sortField === 'level' ? sortDirection : 'asc'}
                      onClick={() => handleSort('level')}
                    >
                      Level
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'coach' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'coach'}
                      direction={sortField === 'coach' ? sortDirection : 'asc'}
                      onClick={() => handleSort('coach')}
                    >
                      Coach(es)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'points' ? sortDirection : false} align="right">
                    <TableSortLabel
                      active={sortField === 'points'}
                      direction={sortField === 'points' ? sortDirection : 'asc'}
                      onClick={() => handleSort('points')}
                    >
                      Total points
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSkaters.map((skater) => (
                  <TableRow 
                    key={skater.id} 
                    hover
                    onClick={() => handleSkaterClick(skater)}
                    sx={{ 
                      cursor: 'pointer',
                      opacity: skater.isPaused ? 0.6 : 1,
                      '&:hover': { bgcolor: 'primary.50' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {skater.firstName} {skater.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {(skater.usfsData?.isCurrentMember === true || skater.usfsData?.isCurrentLTSMember === true) && (
                          <Tooltip title={skater.usfsData?.isCurrentMember ? "USFS Membership Active" : "LTS Membership Active"}>
                            <VerifiedIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          </Tooltip>
                        )}
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {skater.membershipId || '—'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{skater.clubName || skater.club || '—'}</TableCell>
                    {showInterclubColumn && (
                      <TableCell>{skater.interclubName || '—'}</TableCell>
                    )}
                    <TableCell>{skater.currentLevel || skater.startingLevel || '—'}</TableCell>
                    <TableCell>
                      {Array.isArray(skater.coaches) && skater.coaches.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          {skater.coaches.slice(0, 2).map((coach, idx) => (
                            <Typography key={idx} variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {typeof coach === 'string' ? coach : (coach?.displayName || coach?.name || 'Unknown')}
                            </Typography>
                          ))}
                          {skater.coaches.length > 2 && (
                            <Typography variant="caption" color="text.secondary">
                              +{skater.coaches.length - 2} more
                            </Typography>
                          )}
                        </Box>
                      ) : skater.coachName ? (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {skater.coachName}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: skater.totalPoints > 0 ? 600 : undefined }}>
                        {selectedSeason && typeof skater.totalPoints === 'number' ? skater.totalPoints : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={skater.paid ? 'Paid' : 'Unpaid'}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: skater.paid ? 'success.main' : 'warning.main',
                            color: 'white',
                          }}
                        />
                        {skater.isPaused && (
                          <Chip 
                            label="Paused" 
                            size="small" 
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                        {skater.isDisqualified && (
                          <Chip 
                            label="DQ" 
                            size="small" 
                            sx={{ 
                              fontWeight: 600,
                              bgcolor: 'error.main',
                              color: 'white',
                            }}
                          />
                        )}
                        <ChampionshipStatusChips skater={skater} selectedSeason={selectedSeason} />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title={skater.isPaused ? 'Activate for season' : 'Pause for season'}>
                          <IconButton 
                            size="small" 
                            onClick={(e) => { e.stopPropagation(); handleTogglePause(skater); }}
                            sx={{ 
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              color: skater.isPaused ? 'success.main' : 'default',
                              '&:hover': { 
                                bgcolor: skater.isPaused ? 'success.50' : 'grey.100',
                                borderColor: skater.isPaused ? 'success.main' : 'grey.400'
                              }
                            }}
                          >
                            {skater.isPaused ? <PlayIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={skater.isDisqualified ? 'Remove disqualification' : 'Disqualify'}>
                          <IconButton 
                            size="small" 
                            onClick={(e) => { e.stopPropagation(); handleToggleDisqualify(skater); }}
                            sx={{ 
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              color: skater.isDisqualified ? 'success.main' : 'error.main',
                              '&:hover': { 
                                bgcolor: skater.isDisqualified ? 'success.50' : 'error.50',
                                borderColor: skater.isDisqualified ? 'success.main' : 'error.main'
                              }
                            }}
                          >
                            {skater.isDisqualified ? <QualifyIcon fontSize="small" /> : <DisqualifyIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {sortedSkaters.length} of {stats.total} skater{stats.total !== 1 ? 's' : ''}. Click a skater to view competition history.
          </Typography>
        </Box>

        {/* Unregistered Competitors Section */}
        <Box sx={{ mt: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: showUnregistered ? 'warning.main' : 'divider',
              borderRadius: 2,
              bgcolor: showUnregistered ? 'warning.50' : 'grey.50',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'warning.main',
                bgcolor: 'warning.50',
              },
            }}
            onClick={() => setShowUnregistered(!showUnregistered)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: 'warning.main',
                    display: 'flex',
                  }}
                >
                  <PersonAddIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Potential Registrations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Skaters who competed but haven&apos;t registered in the app
                    {unregisteredCompetitors.length > 0 && ` (${unregisteredCompetitors.length} found)`}
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" sx={{ color: 'warning.main' }}>
                {showUnregistered ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Paper>

          {showUnregistered && (
            <Paper
              elevation={0}
              sx={{
                mt: 1,
                p: 2,
                border: '1px solid',
                borderColor: 'warning.200',
                borderRadius: 2,
              }}
            >
              {unregisteredLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : unregisteredCompetitors.length === 0 ? (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  All skaters who competed in eligible events are registered. Great job!
                </Alert>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Alert severity="info" sx={{ borderRadius: 2, flex: 1, mr: 2 }}>
                      <Typography variant="body2">
                        These skaters competed in 2+ competitions at eligible levels but haven&apos;t created an account.
                        Consider reaching out to encourage them to register.
                      </Typography>
                    </Alert>
                    <TextField
                      size="small"
                      placeholder="Search..."
                      value={unregisteredSearch}
                      onChange={(e) => setUnregisteredSearch(e.target.value)}
                      sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <TableContainer
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '& .MuiTableCell-head': {
                        bgcolor: 'warning.50',
                        fontWeight: 700,
                      },
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Competitions</TableCell>
                          <TableCell>Clubs</TableCell>
                          <TableCell>Levels</TableCell>
                          <TableCell align="right">Events</TableCell>
                          <TableCell align="right">Total Points</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredUnregistered.map((competitor, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {competitor.firstName} {competitor.lastName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                {competitor.competitions.slice(0, 3).map((comp, i) => (
                                  <Typography key={i} variant="caption" color="text.secondary">
                                    {comp}
                                  </Typography>
                                ))}
                                {competitor.competitions.length > 3 && (
                                  <Typography variant="caption" color="text.secondary">
                                    +{competitor.competitions.length - 3} more
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {competitor.clubs.slice(0, 2).join(', ')}
                                {competitor.clubs.length > 2 && ` +${competitor.clubs.length - 2}`}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {competitor.levels.slice(0, 2).map((level, i) => (
                                  <Chip key={i} label={level} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                ))}
                                {competitor.levels.length > 2 && (
                                  <Chip label={`+${competitor.levels.length - 2}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={competitor.competitionCount}
                                size="small"
                                color="warning"
                                sx={{ fontWeight: 600, minWidth: 28 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {competitor.totalPoints}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Showing {filteredUnregistered.length} of {unregisteredCompetitors.length} unregistered competitors
                  </Typography>
                </>
              )}
            </Paper>
          )}
        </Box>
      </Paper>

      {/* Skater Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedSkater && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                  }}>
                    <PersonIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {selectedSkater.firstName} {selectedSkater.lastName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={selectedSkater.paid ? 'Paid' : 'Unpaid'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: selectedSkater.paid ? 'success.main' : 'warning.main',
                          color: 'white',
                        }}
                      />
                      {skaterEligibility ? (
                        <Chip
                          label={skaterEligibility.isEligible ? 'Championship eligible' : 'Not championship eligible'}
                          size="small"
                          color={skaterEligibility.isEligible ? 'success' : 'warning'}
                          sx={{ fontWeight: 600 }}
                        />
                      ) : (
                        <ChampionshipStatusChips skater={selectedSkater} selectedSeason={selectedSeason} />
                      )}
                      {(selectedSkater.usfsData?.isCurrentMember === true || selectedSkater.usfsData?.isCurrentLTSMember === true) && (
                        <Chip 
                          icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                          label={selectedSkater.usfsData?.isCurrentMember ? "USFS Active" : "LTS Active"} 
                          size="small" 
                          sx={{ 
                            fontWeight: 600,
                            bgcolor: 'success.50',
                            color: 'success.dark',
                            border: '1px solid',
                            borderColor: 'success.200',
                            '& .MuiChip-icon': { color: 'success.main' }
                          }}
                        />
                      )}
                      {selectedSkater.usfsData?.isCurrentMember === false && selectedSkater.usfsData?.isCurrentLTSMember !== true && (
                        <Chip 
                          label="USFS Expired" 
                          size="small" 
                          sx={{ 
                            fontWeight: 600,
                            bgcolor: 'error.50',
                            color: 'error.dark',
                            border: '1px solid',
                            borderColor: 'error.200',
                          }}
                        />
                      )}
                      {selectedSkater.isPaused && (
                        <Chip label="Paused" size="small" variant="outlined" />
                      )}
                      {selectedSkater.isDisqualified && (
                        <Chip label="DQ" size="small" sx={{ bgcolor: 'error.main', color: 'white' }} />
                      )}
                    </Box>
                  </Box>
                </Box>
                <IconButton onClick={handleCloseDetail} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {/* Membership Info */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Membership Information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BadgeIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">USFS Number</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {(selectedSkater.usfsData?.isCurrentMember === true || selectedSkater.usfsData?.isCurrentLTSMember === true) && (
                          <VerifiedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                          {selectedSkater.membershipId || 'Not set'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BirthdayIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Birth Date</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {(() => {
                          const bd = selectedSkater.birthDate || selectedSkater.dateOfBirth;
                          if (!bd) return 'Not set';
                          if (typeof bd === 'string') {
                            // If it's YYYY-MM-DD format, display as M/D/YYYY
                            if (/^\d{4}-\d{2}-\d{2}$/.test(bd)) {
                              const [y, m, d] = bd.split('-');
                              return `${parseInt(m)}/${parseInt(d)}/${y}`;
                            }
                            return bd;
                          }
                          if (bd._seconds) {
                            // Use UTC to avoid timezone shift
                            const date = new Date(bd._seconds * 1000);
                            return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
                          }
                          if (bd instanceof Date) {
                            return `${bd.getUTCMonth() + 1}/${bd.getUTCDate()}/${bd.getUTCFullYear()}`;
                          }
                          return 'Not set';
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {(selectedSkater.usfsData?.membershipExpiration || selectedSkater.usfsData?.ltsEndDate) && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ExpirationIcon 
                        fontSize="small" 
                        sx={{ 
                          color: (selectedSkater.usfsData?.isCurrentMember === true || selectedSkater.usfsData?.isCurrentLTSMember === true)
                            ? 'success.main' 
                            : (selectedSkater.usfsData?.isCurrentMember === false && selectedSkater.usfsData?.isCurrentLTSMember !== true)
                              ? 'error.main' 
                              : 'action.active' 
                        }} 
                      />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Membership Expires</Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                            color: (selectedSkater.usfsData?.isCurrentMember === true || selectedSkater.usfsData?.isCurrentLTSMember === true)
                              ? 'success.main' 
                              : (selectedSkater.usfsData?.isCurrentMember === false && selectedSkater.usfsData?.isCurrentLTSMember !== true)
                                ? 'error.main' 
                                : 'text.primary'
                          }}
                        >
                          {selectedSkater.usfsData.membershipExpiration || selectedSkater.usfsData.ltsEndDate}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                {/* Level & Club */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Skating Information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Current Level</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedSkater.currentLevel || selectedSkater.startingLevel || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClubIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Club</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedSkater.clubName || selectedSkater.club || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                {/* Coaches */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {selectedSkater.coaches?.length > 1 ? 'Coaches' : 'Coach'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  {Array.isArray(selectedSkater.coaches) && selectedSkater.coaches.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {selectedSkater.coaches.map((coach, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CoachIcon color="action" fontSize="small" />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {typeof coach === 'string' ? coach : (coach?.displayName || coach?.name || 'Unknown')}
                            </Typography>
                            {typeof coach === 'object' && coach?.email && (
                              <Typography variant="caption" color="text.secondary">
                                {coach.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : selectedSkater.coachName ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CoachIcon color="action" fontSize="small" />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedSkater.coachName}
                        </Typography>
                        {selectedSkater.coachEmail && (
                          <Typography variant="caption" color="text.secondary">
                            {selectedSkater.coachEmail}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CoachIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        No coach assigned
                      </Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                {/* Parent/Guardian */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Parent / Guardian
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Name</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedSkater.parentName || selectedSkater.ownerName || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedSkater.parentEmail || selectedSkater.ownerEmail || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {(selectedSkater.parentPhone || selectedSkater.ownerPhone) && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedSkater.parentPhone || selectedSkater.ownerPhone}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Payment Info / Date paid */}
                {selectedSkater.paid && (
                  <>
                    <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Payment Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaymentIcon color="action" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Date paid</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {(() => {
                              const p = selectedSkater.paymentDate || skaterEligibility?.registrationPaidAt;
                              if (!p) return '—';
                              const ms = p._seconds != null ? p._seconds * 1000 : p.toDate ? p.toDate().getTime() : new Date(p).getTime();
                              return Number.isNaN(ms)
                                ? '—'
                                : new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    {skaterEligibility?.requirements?.paymentTiming?.message && (
                      <Grid item xs={12}>
                        <Alert severity="warning" sx={{ borderRadius: 2 }} icon={<WarningIcon />}>
                          {skaterEligibility.requirements.paymentTiming.message}
                        </Alert>
                      </Grid>
                    )}
                  </>
                )}

                {/* Pre-Preliminary Acknowledgment Info */}
                {(selectedSkater.currentLevel === 'Pre-Preliminary' || selectedSkater.startingLevel === 'Pre-Preliminary') && (
                  <>
                    <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Pre-Preliminary Acknowledgment
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      {selectedSkater.prePrelimAcknowledgedAt ? (
                        <Alert severity="info" icon={<WarningIcon />} sx={{ borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Excel Series ineligibility acknowledged
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Acknowledged on {new Date(
                              selectedSkater.prePrelimAcknowledgedAt._seconds 
                                ? selectedSkater.prePrelimAcknowledgedAt._seconds * 1000 
                                : selectedSkater.prePrelimAcknowledgedAt
                            ).toLocaleString()}
                            {selectedSkater.prePrelimAcknowledgedByEmail && (
                              <> by {selectedSkater.prePrelimAcknowledgedByEmail}</>
                            )}
                          </Typography>
                        </Alert>
                      ) : (
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                          <Typography variant="body2">
                            No acknowledgment on file for Pre-Preliminary level restrictions
                          </Typography>
                        </Alert>
                      )}
                    </Grid>
                  </>
                )}

                {/* Competition History Section */}
                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                <Grid item xs={12}>
                  <Button
                    onClick={handleShowCompetitions}
                    startIcon={showCompetitions ? <ExpandLessIcon /> : <TrophyIcon />}
                    endIcon={!showCompetitions && <ExpandMoreIcon />}
                    variant="outlined"
                    fullWidth
                    sx={{ 
                      justifyContent: 'space-between', 
                      borderRadius: 2,
                      py: 1,
                      bgcolor: showCompetitions ? 'primary.50' : 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        Competition History & Points
                      </Typography>
                    </Box>
                  </Button>
                </Grid>

                {showCompetitions && (
                  <Grid item xs={12}>
                    {eligibilityLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : skaterEligibility ? (
                      <Box sx={{ mt: 1 }}>
                        {/* Points Summary */}
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2, mb: 2 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={4}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                                  {skaterEligibility.totalPoints || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Total Points
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={4}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: (skaterEligibility.attendedCompetitions || skaterEligibility.totalCompetitions || 0) > (skaterEligibility.countingCompetitions ?? 0) ? 'warning.main' : 'success.main' }}>
                                  {skaterEligibility.countingCompetitions ?? skaterEligibility.uniqueClubsCompeted ?? 0}
                                  <Typography component="span" variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    /{skaterEligibility.attendedCompetitions || skaterEligibility.totalCompetitions || 0}
                                  </Typography>
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Eligible competitions
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={4}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: 'warning.main' }}>
                                  {skaterEligibility.qualifyingEventsCount || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Qualifying Events
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                          {skaterEligibility.top3Scores && skaterEligibility.top3Scores.length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                Top 3 Scores:
                              </Typography>
                              {skaterEligibility.top3Scores.map((score, idx) => (
                                <Chip
                                  key={idx}
                                  label={score}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          )}
                          {skaterEligibility.requirements?.paymentTiming?.message && (
                            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }} icon={<WarningIcon />}>
                              {skaterEligibility.requirements.paymentTiming.message}
                              {skaterEligibility.registrationPaidAt
                                ? ` Paid ${new Date(skaterEligibility.registrationPaidAt).toLocaleString(undefined, {
                                    dateStyle: 'medium',
                                    timeStyle: 'short',
                                  })}.`
                                : ''}
                            </Alert>
                          )}
                        </Paper>

                        {/* Competition List */}
                        {skaterEligibility.competitionsAttended && skaterEligibility.competitionsAttended.length > 0 ? (
                          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Competition</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>Place</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>Points</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>Counts</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {skaterEligibility.competitionsAttended.map((comp, idx) => (
                                  <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {comp.competitionName || comp.competition || 'Unknown'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          whiteSpace: 'nowrap',
                                          color: String(comp.excludedFromPointsReason || '').includes('after this event ended')
                                            ? 'warning.dark'
                                            : 'text.primary',
                                        }}
                                      >
                                        {formatCompetitionDateRange(comp)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {comp.event || comp.level || '-'}
                                      </Typography>
                                      {comp.eligibility && (
                                        <Chip
                                          label={comp.eligibility}
                                          size="small"
                                          color={comp.eligibility === 'Eligible' ? 'success' : 'default'}
                                          variant="outlined"
                                          sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        label={comp.place || '-'}
                                        size="small"
                                        color={comp.place === 1 ? 'warning' : comp.place <= 3 ? 'primary' : 'default'}
                                        variant={comp.place <= 3 ? 'filled' : 'outlined'}
                                        sx={{ fontWeight: 600, minWidth: 32 }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: comp.counts ? 'success.main' : 'text.secondary' }}>
                                        {comp.points || 0}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      {comp.counts ? (
                                        <Tooltip title="This score counts toward series points">
                                          <QualifyIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                        </Tooltip>
                                      ) : (
                                        <Tooltip
                                          title={
                                            comp.excludedFromPointsReason ||
                                            'This result does not count toward championship series points'
                                          }
                                        >
                                          <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            No competition results found for this skater.
                          </Alert>
                        )}
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Unable to load competition data. The skater may not have any recorded results yet.
                      </Alert>
                    )}
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  color={selectedSkater.isPaused ? 'success' : 'warning'}
                  onClick={() => { handleTogglePause(selectedSkater); handleCloseDetail(); }}
                  startIcon={selectedSkater.isPaused ? <PlayIcon /> : <PauseIcon />}
                  size="small"
                  sx={{ borderRadius: 2 }}
                >
                  {selectedSkater.isPaused ? 'Activate' : 'Pause'}
                </Button>
                <Button 
                  variant="outlined" 
                  color={selectedSkater.isDisqualified ? 'success' : 'error'}
                  onClick={() => { handleToggleDisqualify(selectedSkater); handleCloseDetail(); }}
                  startIcon={selectedSkater.isDisqualified ? <QualifyIcon /> : <DisqualifyIcon />}
                  size="small"
                  sx={{ borderRadius: 2 }}
                >
                  {selectedSkater.isDisqualified ? 'Remove DQ' : 'Disqualify'}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => handleEditClick(selectedSkater)}
                  startIcon={<EditIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Edit Skater
                </Button>
                <Button onClick={handleCloseDetail} variant="outlined" sx={{ borderRadius: 2 }}>
                  Close
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Skater Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCloseEdit} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedSkater && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: 2, 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex'
                  }}>
                    <EditIcon sx={{ color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Edit Skater
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedSkater.firstName} {selectedSkater.lastName}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={handleCloseEdit} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                {/* Club Selection */}
                <FormControl fullWidth>
                  <InputLabel>Club</InputLabel>
                  <Select
                    value={editForm.clubId}
                    label="Club"
                    onChange={(e) => setEditForm(prev => ({ ...prev, clubId: e.target.value }))}
                  >
                    <MenuItem value="">
                      <em>Select a club</em>
                    </MenuItem>
                    {clubs.filter(c => c.isActive !== false).map(club => (
                      <MenuItem key={club.id} value={club.id}>
                        {club.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Level Selection */}
                <FormControl fullWidth>
                  <InputLabel>Level</InputLabel>
                  <Select
                    value={editForm.currentLevel}
                    label="Level"
                    onChange={(e) => {
                      const newLevel = e.target.value;
                      setEditForm(prev => ({ ...prev, currentLevel: newLevel }));
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a level</em>
                    </MenuItem>
                    {ALL_LEVELS.map(level => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Pre-Preliminary Warning */}
                {editForm.currentLevel === 'Pre-Preliminary' && (
                  selectedSkater.prePrelimAcknowledgedAt ? (
                    <Alert severity="info" sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Pre-Preliminary Level Acknowledgment on File
                      </Typography>
                      <Typography variant="body2">
                        Acknowledged on{' '}
                        <strong>
                          {new Date(
                            selectedSkater.prePrelimAcknowledgedAt._seconds 
                              ? selectedSkater.prePrelimAcknowledgedAt._seconds * 1000 
                              : selectedSkater.prePrelimAcknowledgedAt
                          ).toLocaleString()}
                        </strong>
                        {selectedSkater.prePrelimAcknowledgedByEmail && (
                          <> by <strong>{selectedSkater.prePrelimAcknowledgedByEmail}</strong></>
                        )}
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert
                      severity="warning"
                      sx={{
                        borderRadius: 2,
                        '& .MuiAlert-message': { width: '100%' }
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Important: Pre-Preliminary Level Notice
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Skaters at the <strong>Pre-Preliminary</strong> level may want to compete in Excel Series events, however doing so will make them
                        <strong> ineligible for our interclub championship series</strong>. Pre-Preliminary skaters who compete in Excel will be disqualified from qualifying for championships.
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        This acknowledgement must be made by the skater&apos;s parent/guardian through their account.
                      </Typography>
                    </Alert>
                  )
                )}

                {/* Coaches Selection */}
                <Autocomplete
                  multiple
                  freeSolo
                  options={coaches}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.isNew) return option.displayName || '';
                    const name = option.displayName || option.name || option.email || '';
                    const usfs = option.usfsMemberId ? ` • USFS# ${option.usfsMemberId}` : '';
                    const club = option.clubName ? ` • ${option.clubName}` : '';
                    const emailPart = option.email ? ` (${option.email})` : '';
                    return `${name}${emailPart}${usfs}${club}`.trim();
                  }}
                  value={(() => {
                    const norm = (s) => (s || '').trim().toLowerCase();
                    const resolved = editForm.coaches.map((c) => {
                      const match = coaches.find(coach => coach.id === c.coachId || coach.coachDocId === c.coachId || (coach.email && c.email && coach.email === c.email));
                      return match || { ...c, displayName: c.displayName || c.name };
                    });
                    const byName = new Map();
                    resolved.forEach((coach) => {
                      const name = norm(coach.displayName || coach.name || '');
                      const existing = byName.get(name);
                      if (!existing || (coach.email && !existing.email)) byName.set(name, coach);
                    });
                    return Array.from(byName.values());
                  })()}
                  filterOptions={(options, { inputValue }) => {
                    const filtered = options.filter((option) => {
                      const name = (option.displayName || option.name || '').toLowerCase();
                      return name.includes(inputValue.toLowerCase());
                    });
                    // Add option to create new coach if no exact match and input has value
                    if (inputValue && !filtered.some((o) => 
                      (o.displayName || o.name || '').toLowerCase() === inputValue.toLowerCase()
                    )) {
                      filtered.push({
                        inputValue,
                        displayName: `Add "${inputValue}"`,
                        isNew: true,
                      });
                    }
                    return filtered;
                  }}
                  onChange={(e, newValue) => {
                    // Process the new value array
                    const processedCoaches = newValue.map((item) => {
                      // Handle string input (from freeSolo)
                      if (typeof item === 'string') {
                        return {
                          coachId: '',
                          displayName: item,
                          email: '',
                        };
                      }
                      // Handle "Add new" option
                      if (item.isNew) {
                        return {
                          coachId: '',
                          displayName: item.inputValue,
                          email: '',
                        };
                      }
                      // Handle existing coach selection
                      return {
                        coachId: item.id || item.coachId || '',
                        displayName: item.displayName || item.name || '',
                        email: item.email || '',
                      };
                    });
                    setEditForm(prev => ({
                      ...prev,
                      coaches: processedCoaches,
                    }));
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const optionId = option.id || option.coachId;
                    const valueId = value.id || value.coachId;
                    if (optionId && valueId) return optionId === valueId;
                    return (option.displayName || '') === (value.displayName || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Coaches"
                      placeholder={editForm.coaches.length === 0 ? "Search or add coaches..." : ""}
                      helperText="Type to search. Press Enter to add a new coach name."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id || option.coachId || option.displayName || index}
                        label={option.email ? `${option.displayName || option.name || option.email} (${option.email})` : (option.displayName || option.name || option.email)}
                        size="small"
                        icon={<CoachIcon />}
                        sx={{ borderRadius: 2 }}
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.id || option.displayName}>
                      {option.isNew ? (
                        <Typography color="primary" sx={{ fontWeight: 600 }}>
                          {option.displayName}
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {option.displayName || option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[option.usfsMemberId && `USFS# ${option.usfsMemberId}`, option.email, option.clubName].filter(Boolean).join(' • ')}
                          </Typography>
                        </Box>
                      )}
                    </li>
                  )}
                />

                {/* Current Values Display */}
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Current Values
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Club:</strong> {selectedSkater.clubName || 'Not set'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Level:</strong> {selectedSkater.currentLevel || selectedSkater.startingLevel || 'Not set'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Coaches:</strong>{' '}
                      {Array.isArray(selectedSkater.coaches) && selectedSkater.coaches.length > 0 
                        ? selectedSkater.coaches.map(c => typeof c === 'string' ? c : (c?.displayName || c?.name || 'Unknown')).join(', ')
                        : selectedSkater.coachName || 'None assigned'}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseEdit} variant="outlined" sx={{ borderRadius: 2 }}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveEdit} 
                variant="contained" 
                disabled={saving || (editForm.currentLevel === 'Pre-Preliminary' && !selectedSkater.prePrelimAcknowledgedAt)}
                startIcon={saving ? <CircularProgress size={16} /> : null}
                sx={{ borderRadius: 2 }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
